"use client";

import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
// import { usePageContext } from "@/components/ecoBuilder/renderer/PageContext";
import { cn } from "@/lib/utils";
import { createClient } from "@/utils/supabase/client";
import { Loader2 } from "lucide-react";

// Dynamically import PageRenderer to avoid circular dependency
// PageRenderer -> Registry -> EcoPageContent -> PageRenderer
const PageRenderer = dynamic(
    () => import("@/components/ecoBuilder/renderer/PageRenderer").then((mod) => mod.PageRenderer),
    {
        loading: () => <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-muted-foreground" /></div>,
        ssr: false
    }
);

interface EcoPageContentProps {
    className?: string;
    pageId?: string;
    container_width?: "default" | "wide" | "full";
    padding_y?: "none" | "small" | "medium" | "large";
    show_title?: boolean;
}

export const EcoPageContent: React.FC<EcoPageContentProps> = ({
    className,
    pageId,
    container_width = "default",
    padding_y = "medium",
    show_title = true
}) => {
    // const { siteId } = usePageContext(); // siteId is pulled from page data now
    const [pageData, setPageData] = useState<any>(null);
    const [products, setProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // Padding Styles
    const paddingClasses = {
        none: "py-0",
        small: "py-8",
        medium: "py-12 md:py-16",
        large: "py-20 md:py-24"
    };

    // Container Width Styles
    const widthClasses = {
        default: "max-w-4xl px-4",
        wide: "max-w-7xl px-6",
        full: "w-full px-4"
    };

    // Fetch Page Data
    useEffect(() => {
        if (!pageId) {
            setPageData(null);
            return;
        }

        let isMounted = true;

        const fetchData = async () => {
            setLoading(true);
            try {
                const supabase = createClient();

                // 1. Fetch Page
                const { data: page, error } = await supabase
                    .from('pages')
                    .select('*')
                    .eq('id', pageId)
                    .eq('is_visible', true)
                    .single();

                if (error) throw error;

                // 2. Fetch Products (for nested components)
                // Optional: only if we want nested components to have access to products
                const { data: prods } = await supabase
                    .from('products')
                    .select('*')
                    .eq('site_id', page.site_id) // Use page's site_id
                    .limit(20);

                if (isMounted) {
                    setPageData(page);
                    if (prods) setProducts(prods);
                }

            } catch (err) {
                console.error("Error fetching page content:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchData();
        return () => { isMounted = false; };
    }, [pageId]);


    // Case: No Page Selected (Editor Placeholder)
    if (!pageId) {
        return (
            <div className={cn(
                "border-2 border-dashed border-gray-200 rounded-lg text-center flex flex-col items-center justify-center min-h-[200px] bg-gray-50/50",
                paddingClasses[padding_y],
                widthClasses[container_width],
                "mx-auto",
                className
            )}>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-3 text-blue-500">
                    <span className="text-xl">📄</span>
                </div>
                <h3 className="text-lg font-medium text-gray-700">محتوى الصفحة الديناميكي</h3>
                <p className="text-sm text-gray-500 mt-1 max-w-sm">
                    يرجى اختيار الصفحة التي تريد عرض محتواها من الإعدادات.
                </p>
            </div>
        );
    }

    // Case: Loading
    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    // Case: Parse Content
    let parsedContent: any[] = [];
    if (pageData?.published_content) {
        try {
            const raw = pageData.published_content;
            if (typeof raw === 'string') {
                const json = JSON.parse(raw);
                if (Array.isArray(json)) parsedContent = json;
                else if (json.content && Array.isArray(json.content)) parsedContent = json.content;
                else if (json.layers && Array.isArray(json.layers)) parsedContent = json.layers;
            } else if (typeof raw === 'object') {
                if (Array.isArray(raw)) parsedContent = raw;
                else if (Array.isArray((raw as any).content)) parsedContent = (raw as any).content;
            }
        } catch (e) {
            console.error("Error parsing page content", e);
        }
    }

    // Check if padding_y is a preset key
    const isPreset = Object.keys(paddingClasses).includes(padding_y as string);
    const paddingClass = isPreset ? paddingClasses[padding_y as keyof typeof paddingClasses] : "";
    const customStyle = !isPreset ? { paddingTop: padding_y, paddingBottom: padding_y } : {};

    return (
        <div
            className={cn(
                "eco-page-content mx-auto",
                paddingClass,
                container_width === 'full' ? '' : widthClasses[container_width],
                className
            )}
            style={customStyle}
        >
            {(show_title && pageData?.title) && (
                <div className={container_width === 'full' ? widthClasses.default + " mx-auto" : ""}>
                    <h1 className="text-3xl md:text-3xl font-bold mb-8 text-foreground text-start">
                        {pageData.title}
                    </h1>
                </div>
            )}

            {/* 
               We render the raw HTML body first if it exists (for legacy pages), 
               OR render the builder layers if they exist. 
            */}

            {pageData?.body && (
                <div
                    className="prose prose-lg dark:prose-invert max-w-none text-right dir-rtl leading-relaxed mb-8"
                    dir="rtl"
                    dangerouslySetInnerHTML={{ __html: pageData.body }}
                />
            )}

            {parsedContent.length > 0 ? (
                // Render the builder content using PageRenderer
                <PageRenderer
                    layers={parsedContent}
                    products={products}
                    siteId={pageData.site_id}
                // Avoid passing resolvedData to prevent complex client-side resolution issues for now
                />
            ) : (
                !pageData?.body && (
                    <div className="text-center py-8 text-muted-foreground bg-muted/20 rounded-lg">
                        لا يوجد محتوى في هذه الصفحة لعرضه.
                    </div>
                )
            )}
        </div>
    );
};
